import {
  PI_RADIANS_90,
  type Radians,
  getAngularDistance,
} from '../utils/angles';
import { squared } from '../utils/numbers';
import { solveQuadratic } from '../utils/solveQuadratic';
import type { HitArea } from './HitArea';
import { type Line, line } from './line';
import { type Point, point } from './point';
import { type NonZeroVector, type Vector, vector } from './vector';

export const getCirclePointAtAngle = (radius: number, angle: Radians): Point =>
  point.create(radius * Math.cos(angle), radius * Math.sin(angle));

/**
 * Get circle clipping point, assuming center at [0, 0]
 */
export function getCircleClippingPoint(
  radius: number,
  direction: NonZeroVector,
): Point {
  return getCirclePointAtAngle(radius, vector.getAngle(direction));
}

export function getCircleHitArea(radius: number): HitArea {
  return {
    contains: (x, y) => Math.sqrt(x ** 2 + y ** 2) <= radius,
  };
}

export const findCircleLineIntersections = (
  center: Point,
  radius: number,
  ln: Line,
): Point[] => {
  const { x: x0, y: y0 } = center;
  if (line.isVertical(ln)) {
    // y^2 - 2y0y + y0^2 + x^2 - 2x0x + x0^2 - r^2 = 0
    return solveQuadratic(
      1,
      -2 * y0,
      squared(y0) +
        squared(ln.x) -
        2 * ln.x * x0 +
        squared(x0) -
        squared(radius),
    ).map((ySolution) => ({ x: ln.x, y: ySolution }));
  }
  // x^2 - 2x0x + x0^2 + y^2 - 2yy0 + y0^2 = r^2 // substitute y with ax + b
  // x^2 - 2x0x + x0^2 + (ax + b)^2 - 2y0(ax + b) + y0^2 = r^2 // expand binoms
  // x^2 - 2x0x + x0^2 + a^2x^2 + 2abx + b^2 - 2y0ax - 2y0b + y0^2 = r^2 // transform to ax^2 + bx + c
  // (a^2 + 1)x^2 + (-2x0 + 2ab - 2y0a)x + (x0^2 + b^2 - 2y0b + y0^2 - r^2) = 0
  const { a, b } = ln;
  return solveQuadratic(
    squared(a) + 1,
    -2 * x0 + 2 * a * b - 2 * y0 * a,
    squared(x0) + squared(b) - 2 * y0 * b + squared(y0) - squared(radius),
  ).map(line.getPointByX(ln));
};

export const findCircleVectorIntersections = (
  center: Point,
  radius: number,
  v: Vector,
): Point[] => {
  if (!vector.isNonZero(v)) {
    return [];
  }
  const isOnTheVector = (p: Point) =>
    !vector.isNonZero(p) ||
    // we already know the point is on the vector's line and just need to check
    // if it's not on the opposite side, this is why large epsilon is acceptable
    getAngularDistance(vector.getAngle(v), vector.getAngle(p)) < PI_RADIANS_90;

  const ln = line.fromTwoPoints(point.zero, v);
  const intersections = findCircleLineIntersections(center, radius, ln);
  return intersections.filter(isOnTheVector);
};
